import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage, getOutboundGroups, deleteOutboundGroup } from '../../../services';
import { hasFeature } from '../../../services/permissions';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './OutboundCalls.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faPlus, faUsers, faSync, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

function OutboundGroupsList() {
  const getItensStateGlobal = useContext(PbxContext);
  const { setItemsSelectedToEdit } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const history = useHistory();

  const fetchGroups = async () => {
    try {
      const dataUser = await accessLocalStorage.getUserLocalStorage();
      const tenant = dataUser && dataUser.user ? dataUser.user.idtenant : null;

      const response = await getOutboundGroups(tenant, false);
      setGroups(response && response.success && response.data ? response.data : []);
    } catch (error) {
      console.error('Erro ao buscar grupos de saída:', error);
      setGroups([]);
    }
    setLoading(false);
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    await fetchGroups();
  }, [history]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleEdit = (group) => {
    setItemsSelectedToEdit(group);
    history.push('/config/chamadas/saida/grupos/editar');
  };

  const confirmDelete = (group) => {
    setGroupToDelete(group);
    setDeleteError(null);
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    if (!groupToDelete) return;

    const response = await deleteOutboundGroup(groupToDelete.id);
    if (response && response.success) {
      await fetchGroups();
      setShowConfirmDelete(false);
      setGroupToDelete(null);
      return;
    }
    setDeleteError(response && response.error ? response.error : 'Erro ao remover grupo');
  };

  if (loading) return <Loading />;

  return (
    <div className="outbound-container">
      <div className="outbound-header">
        <div className="outbound-title-wrapper">
          <FontAwesomeIcon icon={faUsers} className="outbound-icon" />
          <div>
            <h1 className="outbound-title">Grupos de Saída</h1>
            <p className="outbound-subtitle">Definir por qual tronco cada faixa de ramais faz chamadas de saída</p>
          </div>
        </div>
        <div className="outbound-actions">
          <Link to="/config/chamadas/saida" className="btn-refresh">
            <FontAwesomeIcon icon={faArrowLeft} />
            Chamadas de Saída
          </Link>
          {hasFeature('config.chamadas.saida', 'create') && (
            <Link to="/config/chamadas/saida/grupos/novo" className="btn-new-outbound">
              <FontAwesomeIcon icon={faPlus} />
              Novo Grupo
            </Link>
          )}
        </div>
      </div>

      <div className="filter-card">
        <div className="filter-row">
          <div className="filter-field">
            <label className="filter-label">&nbsp;</label>
            <button className="btn-refresh" onClick={fetchGroups}>
              <FontAwesomeIcon icon={faSync} />
              Atualizar Lista
            </button>
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <p className="empty-state-title">Nenhum grupo de saída cadastrado</p>
          <p className="empty-state-text">
            Crie um grupo para direcionar uma faixa de ramais a um tronco específico. As máscaras de discagem são
            herdadas das configurações de Chamadas de Saída do cliente.
          </p>
        </div>
      ) : (
        <>
          <div className="outbound-table-wrapper">
            <table className="outbound-table">
              <thead className="outbound-table-header">
                <tr>
                  <th>Grupo</th>
                  <th>Identificador</th>
                  <th>Tronco</th>
                  <th>Número apresentado</th>
                  <th>Ramais</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id}>
                    <td><strong>{group.name}</strong></td>
                    <td><span className="outbound-badge badge-mask">{group.slug}</span></td>
                    <td><span className="outbound-badge badge-trunk">{group.trunkName}</span></td>
                    <td>
                      {group.presentedNumber ? (
                        <span className="outbound-badge badge-isp">{group.presentedNumber}</span>
                      ) : (
                        <span className="outbound-badge badge-light">Definido pela operadora</span>
                      )}
                    </td>
                    <td><span className="outbound-badge badge-light">{group.endpointCount}</span></td>
                    <td>
                      <span className={`outbound-badge ${group.active ? 'badge-active' : 'badge-inactive'}`}>
                        {group.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="outbound-actions-cell">
                        <button
                          className="btn-outbound-action btn-edit-outbound"
                          title="Editar e atribuir ramais"
                          onClick={() => handleEdit(group)}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        {hasFeature('config.chamadas.saida', 'delete') && (
                          <button
                            className="btn-outbound-action btn-delete-outbound"
                            title="Excluir"
                            onClick={() => confirmDelete(group)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="outbound-stats">
            <p className="outbound-stats-text">Total: {groups.length} grupo(s)</p>
          </div>
        </>
      )}

      {showConfirmDelete && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowConfirmDelete(false)}></div>
          <div className="modal-card">
            <header className="modal-card-head has-background-danger">
              <p className="modal-card-title has-text-white">
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '10px' }} />
                Confirmar Exclusão
              </p>
              <button className="delete" aria-label="close" onClick={() => setShowConfirmDelete(false)}></button>
            </header>
            <section className="modal-card-body">
              <p>
                Tem certeza que deseja excluir o grupo <strong>{groupToDelete?.name}</strong>?
              </p>
              <p className="mt-2">
                Os ramais atribuídos a este grupo precisam ser devolvidos ao tronco padrão antes da exclusão.
              </p>
              {deleteError && (
                <p className="has-text-danger mt-2"><strong>{deleteError}</strong></p>
              )}
            </section>
            <footer className="modal-card-foot">
              <button className="button is-danger" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                Sim, Excluir
              </button>
              <button className="button" onClick={() => setShowConfirmDelete(false)}>Cancelar</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default OutboundGroupsList;
